import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { UploadCloud, Trash2, LogOut, Loader2, ArrowLeft, Home } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });
const imgUrl = (img) =>
  img && img.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img;

const CATEGORIES = [
  { slug: "uomo", title: "Uomo" },
  { slug: "donna", title: "Donna" },
  { slug: "bimbi", title: "Bimbi" },
  { slug: "terza-eta", title: "Terza Età" },
  { slug: "intimo", title: "Intimo" },
];

function login() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AdminPage() {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=user
  const [category, setCategory] = useState("uomo");
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const loadItems = useCallback(async (cat) => {
    try {
      const r = await api.get(`/products/${cat}`);
      setItems(r.data);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    if (user && user.is_admin) loadItems(category);
  }, [user, category, loadItems]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) {
      toast.error("Seleziona delle immagini");
      return;
    }
    const fd = new FormData();
    fd.append("category", category);
    files.forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      const r = await api.post("/admin/products", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.created.length} foto caricate`);
      await loadItems(category);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/products/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
      toast.success("Prodotto eliminato");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  // ---------- Loading
  if (user === null) {
    return (
      <div className="min-h-screen bg-[#FCFAFB] flex items-center justify-center text-[#123D5B]">
        <Loader2 className="animate-spin" size={26} />
      </div>
    );
  }

  // ---------- Guest / not admin
  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen bg-[#123D5B] flex items-center justify-center p-6">
        <Toaster position="top-center" />
        <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
          <img src="/logo.jpg" alt="logo" className="w-20 h-20 object-contain mx-auto rounded-xl mb-5" />
          <h1 className="font-serif text-3xl text-[#123D5B]">Area Riservata</h1>
          <p className="text-[#4A5863] mt-3 text-sm">
            {user && !user.is_admin
              ? "Questo account non ha i permessi di amministratore."
              : "Accedi con Google per gestire il catalogo prodotti."}
          </p>
          {(!user) ? (
            <button
              onClick={login}
              data-testid="google-login-button"
              className="mt-7 w-full inline-flex items-center justify-center gap-3 bg-[#123D5B] hover:bg-[#0C2A40] text-white rounded-lg px-6 py-3.5 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 5.1 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 5.1 29.5 3 24 3 16 3 9.1 7.6 6.3 14.7z"/><path fill="#4CAF50" d="M24 45c5.2 0 9.9-2 13.5-5.2l-6.2-5.3C29.3 36 26.8 37 24 37c-5.3 0-9.7-2.6-11.3-6.9l-6.5 5C9.1 42.3 16 45 24 45z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.3C41 36.6 45 31 45 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
              Accedi con Google
            </button>
          ) : (
            <button onClick={logout} className="mt-7 w-full bg-[#123D5B] text-white rounded-lg px-6 py-3.5">
              Esci
            </button>
          )}
          <Link to="/" className="inline-flex items-center gap-2 text-[#C24E77] text-sm mt-6">
            <ArrowLeft size={15} /> Torna al sito
          </Link>
        </div>
      </div>
    );
  }

  // ---------- Admin panel
  return (
    <div className="min-h-screen bg-[#FCFAFB]">
      <Toaster position="top-center" />
      <header className="bg-[#123D5B] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="logo" className="w-10 h-10 object-contain rounded-lg" />
            <div>
              <p className="font-serif text-xl leading-none">Gestione Catalogo</p>
              <p className="text-[11px] text-white/60 mt-1">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site" className="hidden sm:inline-flex items-center gap-2 text-sm text-white/80 hover:text-white">
              <Home size={16} /> Vedi sito
            </Link>
            <button onClick={logout} data-testid="logout-button" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-lg px-4 py-2 text-sm transition-colors">
              <LogOut size={16} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {CATEGORIES.map((c) => (
            <button
              key={c.slug}
              onClick={() => setCategory(c.slug)}
              data-testid={`cat-tab-${c.slug}`}
              className={`px-5 py-2.5 rounded-full text-sm transition-all ${
                category === c.slug
                  ? "bg-[#123D5B] text-white"
                  : "bg-white border border-[#123D5B]/15 text-[#123D5B] hover:border-[#C24E77]"
              }`}
            >
              {c.title}
            </button>
          ))}
        </div>

        {/* Uploader */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileRef.current?.click()}
          data-testid="upload-dropzone"
          className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
            drag ? "border-[#C24E77] bg-[#FBEFF3]" : "border-[#123D5B]/20 bg-white hover:border-[#C24E77]"
          }`}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            data-testid="file-input"
            onChange={(e) => handleFiles(e.target.files)}
          />
          {uploading ? (
            <div className="flex flex-col items-center text-[#123D5B]">
              <Loader2 className="animate-spin mb-3" size={30} />
              Caricamento in corso…
            </div>
          ) : (
            <div className="flex flex-col items-center text-[#123D5B]">
              <UploadCloud className="text-[#C24E77] mb-3" size={34} />
              <p className="font-500">Trascina qui le foto oppure tocca per selezionarle</p>
              <p className="text-sm text-[#4A5863] mt-1">
                Puoi selezionare <strong>tante foto insieme</strong>. Verranno aggiunte al reparto <strong>{CATEGORIES.find((c) => c.slug === category)?.title}</strong>.
              </p>
            </div>
          )}
        </div>

        {/* Product grid */}
        <div className="mt-8 flex items-center justify-between">
          <h2 className="font-serif text-2xl text-[#123D5B]">
            {CATEGORIES.find((c) => c.slug === category)?.title} · {items.length} prodotti
          </h2>
        </div>

        {items.length === 0 ? (
          <p className="text-[#4A5863] mt-6">Nessun prodotto in questo reparto. Carica le prime foto!</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-6">
            {items.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className="group relative bg-white rounded-xl overflow-hidden border border-[#123D5B]/10">
                <div className="aspect-[3/4] bg-[#FBEFF3]">
                  <img src={imgUrl(p.image)} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-2.5">
                  <p className="text-xs text-[#123D5B] truncate">{p.name}</p>
                </div>
                <button
                  onClick={() => remove(p.id)}
                  data-testid={`delete-product-${p.id}`}
                  className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white/90 text-[#C24E77] hover:bg-[#C24E77] hover:text-white flex items-center justify-center shadow transition-colors"
                  aria-label="Elimina"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
